import React, { useEffect, useState } from "react";
import { saveNotesForInterviewer } from "../feature/interview/interviewAPI";
import { useDispatch, useSelector } from "react-redux";
import { StickyNote, X, Save } from "lucide-react";

export default function InterviewerNotesCard({ roomId }) {
  const [notes, setNotes] = useState([]);
  const [currentNote, setCurrentNote] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const interviewerNotes = useSelector((state) => state.interview.notes.data);
  const dispatch = useDispatch();

  useEffect(() => {
    if (interviewerNotes) {
      setNotes(interviewerNotes);
    }
  }, [interviewerNotes]);

  const handleSaveNote = () => {
    if (currentNote.trim() === "") return;

    const newNote = {
      roomId: roomId,
      text: currentNote,
      timestamp: Date.now(),
    };
    const { timestamp, ...noteForAPI } = newNote;
    dispatch(saveNotesForInterviewer(noteForAPI));
    setNotes([...notes, newNote]);
    setCurrentNote("");
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && e.ctrlKey) {
      handleSaveNote();
    }
  };

  return (
    <>
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="btn btn-md fixed bottom-4 left-4 rounded-full bg-amber-400 text-cocoa-900 shadow-modal hover:bg-amber-300"
          title="Open interviewer notes"
        >
          <StickyNote className="h-4 w-4" strokeWidth={2.25} />
          Notes
          {notes.length > 0 && (
            <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-cocoa-900/20 px-1.5 text-xs font-bold">
              {notes.length}
            </span>
          )}
        </button>
      )}

      {isOpen && (
        <div className="fixed bottom-4 left-4 z-50 flex h-[500px] w-96 max-w-[calc(100vw-2rem)] animate-fade-in-up flex-col overflow-hidden rounded-2xl border border-white/10 bg-cocoa-800 shadow-modal">
          <div className="flex items-center justify-between border-b border-white/10 bg-cocoa-700 px-5 py-4">
            <h3 className="flex items-center gap-2 text-base font-semibold text-cream-100">
              <StickyNote className="h-4 w-4 text-amber-400" />
              Interview Notes
            </h3>
            <button
              onClick={() => setIsOpen(false)}
              className="flex h-8 w-8 items-center justify-center rounded-lg text-amber-200/70 transition-colors hover:bg-white/10 hover:text-amber-100"
              aria-label="Close notes"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="flex-1 space-y-3 overflow-y-auto p-4">
            {notes.length === 0 ? (
              <p className="pt-10 text-center text-sm text-amber-100/40">
                No notes yet. Capture your first impression below.
              </p>
            ) : (
              notes.map((note) => (
                <div
                  key={note.id}
                  className="rounded-xl border-l-4 border-amber-400 bg-amber-400/10 p-3.5"
                >
                  <p className="text-sm leading-relaxed whitespace-pre-wrap text-amber-50">
                    {note.text}
                  </p>
                  <p className="mt-2 text-xs text-amber-200/50">
                    {new Date(note.timestamp).toLocaleTimeString()}
                  </p>
                </div>
              ))
            )}
          </div>

          <div className="border-t border-white/10 bg-cocoa-700 p-4">
            <textarea
              value={currentNote}
              onChange={(e) => setCurrentNote(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder="Type your note... (Ctrl+Enter to save)"
              className="input min-h-20 resize-none"
              rows="3"
            />
            <button onClick={handleSaveNote} className="btn btn-primary btn-md mt-3 w-full">
              <Save className="h-4 w-4" />
              Save Note
            </button>
          </div>
        </div>
      )}
    </>
  );
}
